import { Text, Flex, Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type ModalFormData } from '../../schemas';
import { InputField } from '@/components/InputField';
import { FunctionComponent, useMemo, useEffect, useRef } from 'react';
import { CircleFileUpload } from '@/components/CircleFileUpload';
import { ImagePreview } from '@/components/ImagePreview';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { Alternatives } from '../Alternatives';
import { renameFile } from '@/utils/image';

type Step2FieldsProps = {
  onStepSuccess?: VoidFunction;
};

export const Step2Fields: FunctionComponent<Step2FieldsProps> = ({ onStepSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const form = useFormContext<ModalFormData>();
  const { fields, append, remove, update } = useFieldArray<ModalFormData>({
    control: form.control,
    name: 'questions',
  });

  const formValues = form.watch();

  const isEditingAnyField = fields?.some(field => field.is_editing);

  const handlePreviewActionClick = (index: number) => {
    const currentField = formValues?.questions?.[index];

    update(index, {
      ...currentField,
      image: undefined,
    });

    setTimeout(() => {
      inputRefs.current[index]?.click();
    }, 100);
  };

  const handlePreviewDeleteClick = (index: number) => {
    const currentField = formValues?.questions?.[index];

    if (inputRefs?.current?.[index]) {
      const input = inputRefs.current[index] as HTMLInputElement;
      input.value = '';
      input.files = null;
    }

    if (currentField) {
      update(index, {
        ...currentField,
        image: undefined,
      });
    }
  };

  const handleSaveQuestion = async (index: number) => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const currentField = formValues?.questions?.[index];
    const hasAnyAlternativeEmpty = currentField?.answer?.some(alternative => !alternative.content);
    const hasAnyAlternativeCorrect = currentField?.answer?.some(alternative => alternative.is_correct);

    if (!!currentField?.title && hasAnyAlternativeCorrect && !hasAnyAlternativeEmpty) {
      update(index, {
        ...currentField,
        is_editing: false,
      });

      return;
    }

    if (!currentField?.title) {
      form.trigger(`questions.${index}.title`);
    }

    if (!hasAnyAlternativeCorrect) {
      form.trigger(`questions.${index}.answer`);
    }

    if (hasAnyAlternativeEmpty) {
      currentField?.answer?.forEach((alternative, alternativeIndex) => {
        if (!alternative?.content) {
          form.trigger(`questions.${index}.answer.${alternativeIndex}.content`);
        }
      });
    }
  };

  const handleCancelQuestion = async (index: number) => {
    const currentField = formValues?.questions?.[index];
    const hasAnyAlternativeEmpty = currentField?.answer?.some(alternative => !alternative.content);
    const hasAnyAlternativeCorrect = currentField?.answer?.some(alternative => alternative.is_correct);
  
    if (!!currentField?.title && hasAnyAlternativeCorrect && !hasAnyAlternativeEmpty) {
      update(index, {
        ...currentField,
        is_editing: false,
      });
      return;
    }
  
    remove(index); 
  
  };

  const handleEditQuestion = (index: number) => {
    const currentField = formValues?.questions?.[index];

    update(index, {
      ...currentField,
      is_editing: true,
    });
  };

  const handleDeleteQuestion = (index: number) => {
    remove(index);
  };

  const handleCancel = () => {
    navigate('/application/challenge');
  };

  const handleNext = async () => {
    const isValid = await form.trigger();

    if (isValid) {
      form.setValue('step', 3);
      onStepSuccess?.();
    }
  };

  const imagePreviewUrls = useMemo(() => {
    return fields.map(field => {
      if (field.image) {
        return field.image instanceof File ? URL.createObjectURL(field.image) : field.image;
      }
      return undefined;
    });
  }, [fields]);



  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviewUrls]);

  return (
    <Flex direction='column' gap={8} maxW={{ base: '100%', md: '50%', lg: '30%' }}>
      <Text fontSize='16px' fontWeight='medium'>
        {t('pages.challenges_details.tabs.tab_2.title_label')}
      </Text>

      <Flex direction='column' gap={8}>
        {fields.map((quizQuestion, index) => (
          <Flex key={quizQuestion.id} direction='column'>
            <Flex gap={3} alignItems='center'>
              {quizQuestion.is_editing && !quizQuestion.image ? (
                <Flex minW='80px' w='80px' minH='80px' h='80px' justifyContent='center' alignItems='center'>
                  <CircleFileUpload
                    ref={el => (inputRefs.current[index] = el)}
                    label={`${t('pages.challenges_details.tabs.tab_2.image_label')}*`}
                    onFileChange={file => {
                      if (!file) return;

                      const currentField = formValues?.questions?.[index];

                             if (currentField) {
                        const extension = file.name.split('.').pop();
                        const renamedFile = renameFile(file, `${quizQuestion.uuid}.${extension}`);

                        const updatedField = {
                          ...currentField,
                          image: renamedFile,
                        };

                        update(index, updatedField);
                      }
                     
                    }}
                  />
                </Flex>
              ) : (
                <ImagePreview
                  src={imagePreviewUrls[index]}
                  actionIconName='image'
                  onActionClick={() => handlePreviewActionClick(index)}
                  onDeleteClick={() => handlePreviewDeleteClick(index)}
                  withoutHover={!quizQuestion.is_editing}
                />
              )}

              {quizQuestion?.is_editing ? (
                <InputField
                  withoutFloating
                  label={`Pergunta ${index + 1}*:`}
                  helperText={`${t('pages.challenges_details.tabs.tab_2.title_helper')}*`}
                  maxLength={100}
                  width='100%'
                  {...form.register(`questions.${index}.title`)}
                  rootProps={{
                    isInvalid: !!form.formState.errors?.questions?.[index]?.title,
                    errorMessage: form.formState.errors?.questions?.[index]?.title?.message,
                  }}
                />
              ) : (
                <Flex direction='column' gap={1} w='100%'>
                  <Text fontWeight='medium'>
                    {t('pages.challenges_details.tabs.tab_2.input_question_label', { number: index + 1 })}*:
                  </Text>
                  <Text>{quizQuestion.title}</Text>
                </Flex>
              )}

              {!quizQuestion?.is_editing ? (
                <Flex gap={4}>
                  <ChakraFeatherIcon
                    name='edit-2'
                    color='brand.500'
                    w='20px'
                    cursor='pointer'
                    onClick={() => handleEditQuestion(index)}
                  />
                  <ChakraFeatherIcon
                    name='trash-2'
                    color='red.500'
                    w='20px'
                    cursor='pointer'
                    onClick={() => handleDeleteQuestion(index)}
                  />
                </Flex>
              ) : null}
            </Flex>

            {quizQuestion?.is_editing ? (
              <Alternatives index={index} isEditing={isEditingAnyField} onSave={() => handleSaveQuestion(index)} onCancel={() => handleCancelQuestion(index)} />
            ) : null}
          </Flex>
        ))}

        {!isEditingAnyField ? (
          <Button
            alignSelf='flex-start'
            variant='link'
            color='brand.500'
            textDecor='underline'
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => {
              form.clearErrors();

              append({
                uuid: crypto.randomUUID(),
                answer: [
                  { uuid: crypto.randomUUID(), content: '', is_correct: false },
                  { uuid: crypto.randomUUID(), content: '', is_correct: false },
                ],
                title: '',
                image: '',
                is_editing: true,
              });
            }}
          >
            <ChakraFeatherIcon name='plus' />
            {t('pages.challenges_details.tabs.tab_2.button_add_question')}
          </Button>
        ) : null}
      </Flex>

      <Flex gap={4} justifyContent={{ md: 'flex-end' }}>
        <Button variant='outlined' color='gray.600' onClick={handleCancel}>
          {t('utils.components.button.cancel')}
        </Button>
        <Button onClick={handleNext} isLoading={form.formState.isValidating}>
          {t('utils.components.button.next')}
        </Button>
      </Flex>
    </Flex>
  );
};
